import { useState } from "react";

import { Controller, useFormContext, useWatch } from "react-hook-form";

import { useValidateCustomAuth } from "src/api/connectApi/queries";
import { SampleRequestConfig } from "src/api/connectApi/types";
import { useWorkspace } from "src/api/queries";
import { ExpandableCodeEditor } from "src/base-components/CodeInput/ExpandableCodeEditor";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import {
  CodeValidationAction,
  type CodeValidationResult,
} from "src/connections/config/CodeValidationAction";
import { ConfigFieldsetCard as FieldsetCard } from "src/connections/config/FieldsetCard";
import { KeyValuePairField } from "src/connections/config/KeyValuePairField";
import {
  DEFAULT_HEADER_ENTRIES,
  type HeaderEntry,
  headerEntriesToRecord,
  SampleHeadersEditor,
} from "src/connections/config/SampleHeadersEditor";
import {
  AvailableEnvironmentPrefixes,
  ConnectionConfigInputs,
} from "src/connections/types";
import { Accordion } from "src/design-system/Accordion";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type Props = {
  environmentPrefix: AvailableEnvironmentPrefixes;
};

const HTTP_METHODS: Array<{ key: string; value: string }> = [
  { key: "GET", value: "GET" },
  { key: "POST", value: "POST" },
  { key: "PUT", value: "PUT" },
  { key: "PATCH", value: "PATCH" },
  { key: "DELETE", value: "DELETE" },
];

const DEFAULT_SAMPLE_REQUEST: Omit<SampleRequestConfig, "headers"> = {
  method: "POST",
  url: "http://localhost/sample-endpoint",
  body: '{"message": "Hello, world!"}',
};

export const CustomCodeFieldset: React.FC<Props> = ({ environmentPrefix }) => {
  const { control, getValues } = useFormContext<ConnectionConfigInputs>();

  const { wsId } = useParamsDecode<DashboardPageParams>();
  const workspace = useWorkspace(wsId);
  const baseUrl = workspace.data?.base_url;
  const validateMutation = useValidateCustomAuth(baseUrl);
  const validationData = validateMutation.data;
  const rawRequest = validationData?.success
    ? validationData.raw_request
    : null;
  const rawRequestText = rawRequest
    ? [
        `Request:`,
        `${rawRequest.method.toUpperCase()} ${rawRequest.url}`,
        `Headers:`,
        ...Object.entries(rawRequest.headers).map(
          ([key, value]) => `  ${key}: ${value}`,
        ),
        `Body:`,
        ...(rawRequest.body ? [rawRequest.body] : []),
      ].join("\n")
    : null;
  const validationResult: CodeValidationResult | undefined = validationData
    ? validationData.success
      ? {
          success: true,
          content: (
            <div className="flex flex-col gap-2">
              <span>
                Code is syntactically valid. Note that we didn't actually make
                the request to the provided endpoint but only generated the raw
                request.
              </span>
              {rawRequestText && (
                <pre className="whitespace-pre-wrap font-mono text-xs">
                  {rawRequestText}
                </pre>
              )}
            </div>
          ),
        }
      : {
          success: false,
          message: validationData.error?.message,
        }
    : undefined;

  const authCode = useWatch({
    control,
    name: `${environmentPrefix}customCodeConfig.auth_code`,
  });
  const hasAuthCode = Boolean(authCode);

  const [sampleRequest, setSampleRequest] = useState<
    Omit<SampleRequestConfig, "headers">
  >(DEFAULT_SAMPLE_REQUEST);
  const [headerEntries, setHeaderEntries] = useState<HeaderEntry[]>(
    DEFAULT_HEADER_ENTRIES,
  );

  const handleValidate = () => {
    const authCode = getValues(
      `${environmentPrefix}customCodeConfig.auth_code`,
    );
    if (!authCode) return;
    const currentSecrets = getValues(`${environmentPrefix}secrets`);
    const secretsDict: Record<string, string> = Object.fromEntries(
      (currentSecrets ?? []).flatMap((s) =>
        s.key && s.value ? [[s.key, s.value] as const] : [],
      ),
    );
    validateMutation.mutate({
      auth_code: authCode,
      secrets: secretsDict,
      sample_request: {
        ...sampleRequest,
        headers: headerEntriesToRecord(headerEntries),
      },
    });
  };

  return (
    <FloatingWindowsProvider>
      <FieldsetCard>
        <FormItem gap="sm">
          <KeyValuePairField
            cta="Add secret"
            name={`${environmentPrefix}secrets`}
            showHeader={false}
            isSensitive
          />
        </FormItem>
      </FieldsetCard>
      <FieldsetCard>
        <FormItem
          description="Write a Python function that authenticates the request. The function must be named 'authenticate' and accept (request, secrets) parameters."
          gap="sm"
          label="Authentication code"
          isRequired
        >
          <div className="mb-2">
            <Accordion dataLoc="sample-request-accordion">
              <Accordion.Item
                label="Configure sample request"
                value="sample-request"
              >
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <div className="w-28">
                      <FixedSimpleDropdown
                        buttonDataLoc="sample-request-method"
                        dataLoc="sample-request-method-dropdown"
                        elements={HTTP_METHODS}
                        placeholder="Method"
                        selectedKey={sampleRequest.method ?? "POST"}
                        onSelect={(method) =>
                          setSampleRequest((prev) => ({
                            ...prev,
                            method: method as SampleRequestConfig["method"],
                          }))
                        }
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="http://localhost/sample-endpoint"
                        value={sampleRequest.url ?? ""}
                        fullWidth
                        onChange={(e) =>
                          setSampleRequest((prev) => ({
                            ...prev,
                            url: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <FormItem gap="xs" label="Headers">
                    <SampleHeadersEditor
                      dataLoc="sample-request-headers"
                      entries={headerEntries}
                      onChange={setHeaderEntries}
                    />
                  </FormItem>
                  <FormItem gap="xs" label="Body">
                    <ExpandableCodeEditor
                      dataLoc="sample-request-body-editor"
                      expandedHeight="h-130"
                      expandedWidth="w-160"
                      language="json"
                      placeholder='{"message": "Hello, world!"}'
                      title="Request body"
                      value={sampleRequest.body ?? ""}
                      onChange={(value) =>
                        setSampleRequest((prev) => ({
                          ...prev,
                          body: value ?? "",
                        }))
                      }
                    />
                  </FormItem>
                </div>
              </Accordion.Item>
            </Accordion>
          </div>
          <Controller
            control={control}
            name={`${environmentPrefix}customCodeConfig.auth_code`}
            rules={{ required: "Authentication code is required" }}
            render={({ field }) => (
              <ExpandableCodeEditor
                dataLoc={`${environmentPrefix}custom-code-auth-editor`}
                expandedHeight="h-195"
                expandedWidth="w-250"
                height="h-96"
                language="python"
                placeholder="Write your authentication code here..."
                title="Authentication code"
                value={field.value ?? ""}
                onBlur={field.onBlur}
                onChange={field.onChange}
              />
            )}
          />
        </FormItem>
        <CodeValidationAction
          dataLoc="validate-custom-auth-button"
          disabled={!hasAuthCode || !baseUrl}
          loading={validateMutation.isPending}
          requestError={validateMutation.error}
          result={validationResult}
          onValidate={handleValidate}
        />
      </FieldsetCard>
    </FloatingWindowsProvider>
  );
};
